import React, { forwardRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import initActionCreators from '../../redux/TimetablesRedux';
import { SEARCH_MODE } from '../../services/TimetablesService';
import { CATEGORIES, TIMETABLES_ACTION, logEvent } from '../../services/AnalyticsService';

import { ReactComponent as IconBus } from '../../assets/svg/bus.svg';
import { ReactComponent as IconFerry } from '../../assets/svg/ferry.svg';
import { ReactComponent as IconTrain } from '../../assets/svg/train.svg';
import { ReactComponent as IconClose } from '../../assets/svg/icon-tick-circle.svg';

const bem = 'tt-transport-mode';

const iconClassName = (forIcon, searchMode) => {
  const iconBem = `${bem}__icons-item`;
  let iconName = `${iconBem} ${iconBem}--${forIcon.toLowerCase()}`;
  if (forIcon === searchMode) {
    iconName += ` ${iconBem}--selected`;
  }
  return iconName;
};

const TransportModeItem = forwardRef(({
  ariaLabel,
  onClick,
  onKeyDown,
  searchMode,
  selectedSearchMode,
  SvgIcon,
}, ref) => (
  <li
    aria-label={ariaLabel}
    aria-selected={selectedSearchMode === searchMode}
    className={iconClassName(searchMode, selectedSearchMode)}
    data-testid={`tm-${searchMode}`}
    id={`tm-${searchMode}`}
    onClick={onClick}
    onKeyDown={onKeyDown}
    ref={ref}
    role="tab"
    tabIndex={-1}
  >
    <IconClose className="svg-tick" role="img" tabIndex={-1} />
    <SvgIcon className="svg" role="img" tabIndex={-1} />
  </li>
));

TransportModeItem.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  searchMode: PropTypes.string.isRequired,
  selectedSearchMode: PropTypes.string.isRequired,
  SvgIcon: PropTypes.object.isRequired,
};

class TransportMode extends React.PureComponent {
  constructor(props) {
    super(props);
    const { location, changeTransportMode } = props;
    const query = new URLSearchParams(location.search);
    if (query.get('route_type')) {
      changeTransportMode(query.get('route_type'));
    }

    this.busRef = React.createRef();
    this.trainRef = React.createRef();
    this.ferryRef = React.createRef();
  }

  /**
   * Enables traversing and changing the transport mode using keyboard.
   */
  switchTabOnKeyDown = (ev) => {
    ev.stopPropagation();
    const { searchMode: curSearchMode } = this.props;
    const searchModeArr = Object.keys(SEARCH_MODE);
    const curIndex = searchModeArr.indexOf(curSearchMode);
    let nextIndex;

    switch (ev.key) {
      case 'ArrowRight':
        nextIndex = (curIndex >= searchModeArr.length - 1) ? 0 : curIndex + 1;
        this.changeMode(searchModeArr[nextIndex]);
        break;
      case 'ArrowLeft':
        nextIndex = (curIndex <= 0) ? searchModeArr.length - 1 : curIndex - 1;
        this.changeMode(searchModeArr[nextIndex]);
        break;
      default:
        break;
    }
  };

  changeMode = (switchToMode) => {
    const { changeTransportMode } = this.props;
    logEvent(CATEGORIES.TIMETABLES, TIMETABLES_ACTION.TRANSPORT_MODE, switchToMode);
    switch (switchToMode) {
      case SEARCH_MODE.BUS:
        this.busRef.current.focus();
        break;
      case SEARCH_MODE.TRAIN:
        this.trainRef.current.focus();
        break;
      case SEARCH_MODE.FERRY:
        this.ferryRef.current.focus();
        break;
      default:
        break;
    }
    changeTransportMode(switchToMode);
  };

  onClickBus = () => this.changeMode(SEARCH_MODE.BUS);

  onClickTrain = () => this.changeMode(SEARCH_MODE.TRAIN);

  onClickFerry = () => this.changeMode(SEARCH_MODE.FERRY);

  render() {
    const { searchMode } = this.props;

    return (
      <div className={bem}>
        <div role="none" className={`${bem}__label`}>Transport mode</div>

        <ul
          aria-activedescendant={`tm-${searchMode}`}
          aria-label="Transport mode"
          className={`${bem}__icons`}
          onKeyDown={this.switchTabOnKeyDown}
          role="tablist"
          tabIndex={0}
        >
          <TransportModeItem
            ariaLabel="bus"
            SvgIcon={IconBus}
            onClick={this.onClickBus}
            onKeyDown={this.switchTabOnKeyDown}
            ref={this.busRef}
            searchMode={SEARCH_MODE.BUS}
            selectedSearchMode={searchMode}
          />
          <TransportModeItem
            ariaLabel="train"
            SvgIcon={IconTrain}
            onClick={this.onClickTrain}
            onKeyDown={this.switchTabOnKeyDown}
            ref={this.trainRef}
            searchMode={SEARCH_MODE.TRAIN}
            selectedSearchMode={searchMode}
          />
          <TransportModeItem
            ariaLabel="ferry"
            SvgIcon={IconFerry}
            onClick={this.onClickFerry}
            onKeyDown={this.switchTabOnKeyDown}
            ref={this.ferryRef}
            searchMode={SEARCH_MODE.FERRY}
            selectedSearchMode={searchMode}
          />
        </ul>

        <ul className={`${bem}__text`} role="none" tabIndex={-1}>
          <li className={`${bem}__text-item`} onClick={this.onClickBus} role="none" data-cy="transport-mode-item-bus" tabIndex={-1}>Bus</li>
          <li className={`${bem}__text-item`} onClick={this.onClickTrain} role="none" data-cy="transport-mode-item-train" tabIndex={-1}>Train</li>
          <li className={`${bem}__text-item`} onClick={this.onClickFerry} role="none" data-cy="transport-mode-item-ferry" tabIndex={-1}>Ferry</li>
        </ul>

      </div>
    );
  }
}

/* ------------- Prop Types Definitions  ------------- */
TransportMode.propTypes = {
  searchMode: PropTypes.string.isRequired,
  changeTransportMode: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};
/* ------------- Redux mappings  ------------- */
const mapDispatchToProps = dispatch => ({
  changeTransportMode: mode => dispatch(initActionCreators().changeTransportMode(mode)),
});
const mapStateToProps = state => ({
  searchMode: state.timetables.searchMode,
});
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TransportMode));
